/**
 * Pairing-code sign-in and the in-memory session (spec §6).
 *
 * 1. `choose(product)` -> `pair/start` -> show the code + connect URL.
 * 2. Poll `pair/poll` every `poll_interval_ms` (POST body) until approved, denied, consumed or expired.
 * 3. On approval: check the token is a Word token for THIS product, `GET session`, then wait in `confirm`
 *    ("Connected as <email> (<product>)") until the user clicks Confirm — nothing is uploaded before that.
 * 4. Once confirmed, the token is kept in the pane origin's localStorage (never the URL, never the document) so
 *    closing Word does not sign the user out. `restore()` re-checks it with `GET session` on the next start and
 *    drops it if it expired, was revoked, or names another product. It still dies with the parent web session
 *    and at its own lifetime cap (Ask ≤ 8 h).
 * 5. `signOut()` revokes (best effort), wipes memory and storage and reloads the pane. Switching product = sign out.
 */

import { PRODUCTS, type Product } from "./config";
import { createAskBackend } from "./backends/ask";
import { createFirmBackend } from "./backends/firm";
import { BackendError, type WordBackend, WrongProductError } from "./backends/types";

export type SessionState =
  | { phase: "choose" }
  | { phase: "starting"; product: Product }
  | { phase: "pairing"; product: Product; userCode: string; connectUrl: string; expiresAt: number }
  | { phase: "verifying"; product: Product }
  | { phase: "confirm"; product: Product; email: string }
  | { phase: "connected"; product: Product; email: string }
  | { phase: "error"; product: Product | null; message: string; reconnect?: boolean };

export interface SessionDeps {
  backends?: Partial<Record<Product, () => WordBackend>>;
  /** Epoch milliseconds. */
  now?: () => number;
  sleep?: (ms: number) => Promise<void>;
  /** Full pane reload after sign-out (wipes every in-memory trace). */
  reload?: () => void;
  /** Where a confirmed sign-in is kept across Word restarts; null keeps it in memory only. */
  storage?: TokenStorage | null;
}

export type TokenStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

const STORAGE_KEY = "ella.word.session";

interface StoredSession {
  product: Product;
  token: string;
  expiresAt: number | null;
  email: string;
}

/** localStorage when the webview allows it; a private window or blocked storage just means re-pairing. */
function defaultStorage(): TokenStorage | null {
  try {
    return typeof window !== "undefined" && window.localStorage ? window.localStorage : null;
  } catch {
    return null;
  }
}

/** Consecutive network failures tolerated while polling before giving up. */
const MAX_POLL_FAILURES = 5;

function describe(error: unknown): string {
  if (error instanceof WrongProductError || error instanceof BackendError) return error.message;
  if (error instanceof Error) return `Could not reach Jurisio: ${error.message}`;
  return "Something went wrong.";
}

export class PairingSession {
  private state: SessionState = { phase: "choose" };
  private readonly listeners = new Set<() => void>();
  private backend: WordBackend | null = null;
  private token: string | null = null;
  private tokenExpiresAt: number | null = null;
  /** Bumped on cancel/sign-out so a running poll loop stops. */
  private generation = 0;
  private readonly deps: Required<Omit<SessionDeps, "backends" | "storage">> & { backends: Record<Product, () => WordBackend> };
  private readonly storage: TokenStorage | null;

  constructor(deps: SessionDeps = {}) {
    this.deps = {
      backends: { ask: () => createAskBackend(), firm: () => createFirmBackend(), ...deps.backends },
      now: deps.now ?? (() => Date.now()),
      sleep: deps.sleep ?? ((ms) => new Promise((resolve) => setTimeout(resolve, ms))),
      reload: deps.reload ?? (() => window.location.reload()),
    };
    this.storage = deps.storage === undefined ? defaultStorage() : deps.storage;
  }

  // ---- persistence -----------------------------------------------------------------------------------------

  private readStored(): StoredSession | null {
    try {
      const raw = this.storage?.getItem(STORAGE_KEY);
      if (!raw) return null;
      const value = JSON.parse(raw) as Partial<StoredSession>;
      if (
        (value.product !== "ask" && value.product !== "firm") ||
        typeof value.token !== "string" ||
        typeof value.email !== "string" ||
        (value.expiresAt !== null && typeof value.expiresAt !== "number")
      ) {
        this.clearStored();
        return null;
      }
      return value as StoredSession;
    } catch {
      this.clearStored();
      return null;
    }
  }

  private writeStored(value: StoredSession): void {
    try {
      this.storage?.setItem(STORAGE_KEY, JSON.stringify(value));
    } catch {
      // Storage full or blocked: the session still works for this pane; the next start re-pairs.
    }
  }

  private clearStored(): void {
    try {
      this.storage?.removeItem(STORAGE_KEY);
    } catch {
      // Nothing to clear.
    }
  }

  /**
   * Resume a confirmed sign-in from an earlier pane (Word restart, pane reopen). The server re-checks the
   * token, so a revoked, expired, or web-signed-out session falls back to the chooser.
   */
  async restore(): Promise<void> {
    if (this.state.phase !== "choose" || this.token !== null) return;
    const stored = this.readStored();
    if (stored === null) return;
    if (stored.expiresAt !== null && this.deps.now() >= stored.expiresAt * 1000) {
      this.clearStored();
      return;
    }
    const generation = ++this.generation;
    const backend = this.deps.backends[stored.product]();
    if (backend.config.product !== stored.product) {
      this.clearStored();
      return;
    }
    this.set({ phase: "verifying", product: stored.product });
    try {
      const session = await backend.getSession(stored.token);
      if (generation !== this.generation) return;
      if (session.product !== stored.product) throw new WrongProductError("This session belongs to a different Jurisio product.");
      this.backend = backend;
      this.token = stored.token;
      this.tokenExpiresAt = stored.expiresAt;
      this.set({ phase: "connected", product: stored.product, email: session.email });
    } catch (error) {
      if (generation !== this.generation) return;
      // Only a refusal proves the token is dead; a network blip or a 5xx keeps it for the next start.
      const refused = error instanceof WrongProductError || (error instanceof BackendError && error.status >= 400 && error.status < 500);
      if (refused) this.clearStored();
      this.set({ phase: "choose" });
    }
  }

  // ---- store (for React's useSyncExternalStore) -----------------------------------------------------------

  getState = (): SessionState => this.state;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  private set(state: SessionState): void {
    this.state = state;
    for (const listener of this.listeners) listener();
  }

  // ---- pairing -----------------------------------------------------------------------------------------------

  async choose(product: Product): Promise<void> {
    if (this.token !== null) {
      // Switching products is a full sign-out (spec §0.2).
      await this.signOut();
      return;
    }
    const generation = ++this.generation;
    const backend = this.deps.backends[product]();
    if (backend.config.product !== product) throw new WrongProductError("Backend/product mismatch.");
    this.backend = backend;
    this.set({ phase: "starting", product });
    let start;
    try {
      start = await backend.startPair();
    } catch (error) {
      if (generation === this.generation) this.set({ phase: "error", product, message: describe(error) });
      return;
    }
    if (generation !== this.generation) return;
    this.set({ phase: "pairing", product, userCode: start.userCode, connectUrl: PRODUCTS[product].connectUrl, expiresAt: start.expiresAt });
    await this.pollLoop(generation, product, start.pairId, start.pollSecret, start.pollIntervalMs, start.expiresAt);
  }

  private async pollLoop(generation: number, product: Product, pairId: string, pollSecret: string, intervalMs: number, expiresAt: number): Promise<void> {
    const backend = this.backend as WordBackend;
    let failures = 0;
    let wait = intervalMs;
    for (;;) {
      await this.deps.sleep(wait);
      if (generation !== this.generation) return;
      if (this.deps.now() >= expiresAt * 1000) {
        this.set({ phase: "error", product, message: "The code expired. Start again to get a new one." });
        return;
      }
      let result;
      try {
        result = await backend.pollPair(pairId, pollSecret);
        failures = 0;
      } catch (error) {
        if (generation !== this.generation) return;
        if (error instanceof WrongProductError || ++failures >= MAX_POLL_FAILURES) {
          this.set({ phase: "error", product, message: describe(error) });
          return;
        }
        wait = intervalMs;
        continue;
      }
      if (generation !== this.generation) return;
      switch (result.status) {
        case "pending":
          wait = intervalMs;
          continue;
        case "slow_down":
          wait = Math.max(intervalMs, result.retryAfterMs);
          continue;
        case "approved":
          await this.verify(generation, product, result.token, result.expiresAt);
          return;
        case "denied":
          this.set({ phase: "error", product, message: "The sign-in was declined." });
          return;
        case "consumed":
          this.set({ phase: "error", product, message: "This code was already used. Start again." });
          return;
        case "expired":
          this.set({ phase: "error", product, message: "The code expired. Start again to get a new one." });
          return;
      }
    }
  }

  private async verify(generation: number, product: Product, token: string, expiresAt: number | null): Promise<void> {
    const backend = this.backend as WordBackend;
    this.set({ phase: "verifying", product });
    try {
      const session = await backend.getSession(token);
      if (generation !== this.generation) return;
      if (session.product !== product) throw new WrongProductError("This session belongs to a different Jurisio product.");
      this.token = token;
      this.tokenExpiresAt = expiresAt;
      this.set({ phase: "confirm", product, email: session.email });
    } catch (error) {
      if (generation === this.generation) this.set({ phase: "error", product, message: describe(error) });
    }
  }

  /** Stop pairing (or waiting to confirm) and go back to the product chooser. */
  cancel(): void {
    this.generation++;
    this.token = null;
    this.tokenExpiresAt = null;
    this.backend = null;
    this.clearStored();
    this.set({ phase: "choose" });
  }

  /** The user confirmed "Connected as <email>". Only now may anything be uploaded. */
  confirm(): void {
    if (this.state.phase !== "confirm") return;
    if (this.token !== null) {
      this.writeStored({ product: this.state.product, token: this.token, expiresAt: this.tokenExpiresAt, email: this.state.email });
    }
    this.set({ phase: "connected", product: this.state.product, email: this.state.email });
  }

  // ---- connected -------------------------------------------------------------------------------------------

  /** The bearer token, only once the user confirmed the account and while it has not expired. */
  authorizedToken(): string | null {
    if (this.state.phase !== "connected" || this.token === null) return null;
    if (this.tokenExpiresAt !== null && this.deps.now() >= this.tokenExpiresAt * 1000) {
      this.expire();
      return null;
    }
    return this.token;
  }

  get product(): Product | null {
    return this.state.phase === "choose" ? null : (this.state as { product: Product | null }).product;
  }

  private expire(): void {
    const product = this.product;
    this.generation++;
    this.token = null;
    this.tokenExpiresAt = null;
    this.clearStored();
    this.set({ phase: "error", product, message: "Your Word session ended. Connect again (about 15 seconds).", reconnect: product !== null });
  }

  /** A backend call was refused with 401: drop the token and offer Reconnect. */
  sessionLost(): void {
    if (this.state.phase !== "connected") return;
    this.expire();
  }

  /** Pair again with the same product (after the session ended). The pane's chat stays in memory. */
  async reconnect(): Promise<void> {
    const product = this.product;
    this.cancel();
    if (product !== null) await this.choose(product);
  }

  /**
   * Keep the parent web session alive. Call ONLY from explicit user actions (send, apply, open card),
   * never from polling or timers (spec §6.1).
   */
  async touch(): Promise<void> {
    const token = this.authorizedToken();
    if (token === null || this.backend === null) return;
    try {
      await this.backend.activity(token);
    } catch (error) {
      if (error instanceof BackendError && (error.status === 401 || error.status === 403)) this.expire();
    }
  }

  /** Revoke (best effort), wipe the token from memory and reload the pane. */
  async signOut(): Promise<void> {
    const token = this.token;
    const backend = this.backend;
    this.generation++;
    this.token = null;
    this.tokenExpiresAt = null;
    this.backend = null;
    this.clearStored();
    this.set({ phase: "choose" });
    if (token !== null && backend !== null) {
      try {
        await backend.revoke(token);
      } catch {
        // The token expires on its own; the server-side list lets the user revoke it from the web app.
      }
    }
    this.deps.reload();
  }

  /** For tests: whether a token is held in memory at all. */
  hasToken(): boolean {
    return this.token !== null;
  }
}
